const INTERNAL_LINK_FLAG = '1';
const EMBEDDED_BODY_CACHE_LIMIT = 250;
const embeddedBodyHtmlCache = new Map();

const escapeHtml = (value = '') =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const INTERNAL_LINK_ATTR = 'data-amn-link';
export const INTERNAL_LINK_TYPE_ATTR = 'data-amn-link-type';
export const INTERNAL_LINK_ID_ATTR = 'data-amn-link-id';

export const getInternalEntityHref = (targetType, targetId) => {
  if (!targetId) return '#';
  return targetType === 'folder' ? `/sets/${targetId}` : `/tune/${targetId}`;
};

export const normalizeInternalLinkType = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'folder' ? 'folder' : 'note';
};

export const buildInternalLinkHtml = ({ targetType = 'note', targetId = '', title = '' }) => {
  const safeType = normalizeInternalLinkType(targetType);
  const safeId = String(targetId || '').trim();
  if (!safeId) return '';
  const safeTitle = String(title || '').trim() || (safeType === 'folder' ? 'Untitled Folder' : 'Untitled Note');
  const safeHref = getInternalEntityHref(safeType, safeId);
  return `<span class="internal-note-link" role="link" tabindex="0" contenteditable="false" data-amn-href="${escapeHtml(safeHref)}" ${INTERNAL_LINK_ATTR}="${INTERNAL_LINK_FLAG}" ${INTERNAL_LINK_TYPE_ATTR}="${safeType}" ${INTERNAL_LINK_ID_ATTR}="${escapeHtml(safeId)}">${escapeHtml(safeTitle)}</span>`;
};

export const normalizeInternalLinkMarkup = (html = '') => {
  const raw = String(html || '');
  if (!raw || !raw.includes('data-amn-link')) return raw;

  if (typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
    try {
      const doc = new window.DOMParser().parseFromString(`<div>${raw}</div>`, 'text/html');
      const nodes = doc.querySelectorAll(`[${INTERNAL_LINK_ATTR}="${INTERNAL_LINK_FLAG}"]`);
      nodes.forEach((node) => {
        const targetType = normalizeInternalLinkType(node.getAttribute(INTERNAL_LINK_TYPE_ATTR));
        const targetId = String(node.getAttribute(INTERNAL_LINK_ID_ATTR) || '').trim();
        if (!targetId) return;
        const next = doc.createElement('span');
        next.setAttribute('class', 'internal-note-link');
        next.setAttribute('role', 'link');
        next.setAttribute('tabindex', '0');
        next.setAttribute('contenteditable', 'false');
        next.setAttribute(INTERNAL_LINK_ATTR, INTERNAL_LINK_FLAG);
        next.setAttribute(INTERNAL_LINK_TYPE_ATTR, targetType);
        next.setAttribute(INTERNAL_LINK_ID_ATTR, targetId);
        next.setAttribute('data-amn-href', getInternalEntityHref(targetType, targetId));
        next.textContent = (node.textContent || '').trim();
        node.replaceWith(next);
      });
      return doc.body.innerHTML.replace(/^<div>|<\/div>$/g, '');
    } catch (err) {
      console.warn('Failed to normalize internal link markup:', err);
    }
  }

  return raw.replace(
    /<a\b([^>]*\bdata-amn-link\s*=\s*["']1["'][^>]*)>(.*?)<\/a>/gi,
    (_m, attrs = '', inner = '') => {
      const idMatch = String(attrs).match(/data-amn-link-id\s*=\s*["']([^"']+)["']/i);
      const typeMatch = String(attrs).match(/data-amn-link-type\s*=\s*["']([^"']+)["']/i);
      const targetId = String(idMatch?.[1] || '').trim();
      if (!targetId) return inner;
      const targetType = normalizeInternalLinkType(typeMatch?.[1] || 'note');
      const safeTitle = String(inner || '').replace(/<[^>]*>/g, '').trim();
      return buildInternalLinkHtml({ targetType, targetId, title: safeTitle });
    }
  );
};

export const getInternalLinkPayloadFromElement = (el) => {
  if (!el || typeof el.getAttribute !== 'function') return null;
  if (el.getAttribute(INTERNAL_LINK_ATTR) !== INTERNAL_LINK_FLAG) return null;
  const targetType = normalizeInternalLinkType(el.getAttribute(INTERNAL_LINK_TYPE_ATTR));
  const targetId = String(el.getAttribute(INTERNAL_LINK_ID_ATTR) || '').trim();
  if (!targetId) return null;
  const title = (el.textContent || '').trim();
  return {
    targetType,
    targetId,
    title,
    href: getInternalEntityHref(targetType, targetId),
  };
};

export const getInternalLinkElementFromEventTarget = (target) => {
  if (!target) return null;
  const node = target?.nodeType === 3 ? target.parentElement : target;
  if (!node || typeof node.closest !== 'function') return null;
  return node.closest?.('[data-amn-link="1"]') || null;
};

const parseViaDom = (html = '') => {
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return [];
  const raw = String(html || '').trim();
  if (!raw) return [];
  const doc = new window.DOMParser().parseFromString(`<div>${raw}</div>`, 'text/html');
  const nodes = doc.querySelectorAll(`[${INTERNAL_LINK_ATTR}="${INTERNAL_LINK_FLAG}"][${INTERNAL_LINK_ID_ATTR}]`);
  return Array.from(nodes)
    .map(getInternalLinkPayloadFromElement)
    .filter(Boolean);
};

const parseViaRegex = (html = '') => {
  const raw = String(html || '');
  if (!raw) return [];
  const results = [];
  const linkRegex = /<(?:a|span)\b[^>]*data-amn-link\s*=\s*["']1["'][^>]*>(.*?)<\/(?:a|span)>/gi;
  let linkMatch;
  while ((linkMatch = linkRegex.exec(raw)) !== null) {
    const wholeTag = linkMatch[0] || '';
    const inner = (linkMatch[1] || '').replace(/<[^>]*>/g, '').trim();
    const idMatch = wholeTag.match(/data-amn-link-id\s*=\s*["']([^"']+)["']/i);
    const typeMatch = wholeTag.match(/data-amn-link-type\s*=\s*["']([^"']+)["']/i);
    const targetId = String(idMatch?.[1] || '').trim();
    if (!targetId) continue;
    const targetType = normalizeInternalLinkType(typeMatch?.[1] || 'note');
    results.push({
      targetType,
      targetId,
      title: inner,
      href: getInternalEntityHref(targetType, targetId),
    });
  }
  return results;
};

export const extractInternalLinksFromHtml = (html = '') => {
  const parsed = parseViaDom(html);
  if (parsed.length > 0) return parsed;
  return parseViaRegex(html);
};

export const removeInternalLinksToTargetFromHtml = ({ html = '', targetType = 'note', targetId = '' } = {}) => {
  const raw = String(html || '');
  const safeId = String(targetId || '').trim();
  const safeType = normalizeInternalLinkType(targetType);
  if (!raw || !safeId || !raw.includes(INTERNAL_LINK_ATTR)) return raw;

  if (typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
    try {
      const doc = new window.DOMParser().parseFromString(`<div>${raw}</div>`, 'text/html');
      const links = doc.querySelectorAll(`[${INTERNAL_LINK_ATTR}="${INTERNAL_LINK_FLAG}"]`);
      links.forEach((linkEl) => {
        const linkType = normalizeInternalLinkType(linkEl.getAttribute(INTERNAL_LINK_TYPE_ATTR));
        const linkId = String(linkEl.getAttribute(INTERNAL_LINK_ID_ATTR) || '').trim();
        if (linkType !== safeType || linkId !== safeId) return;

        const nextSibling = linkEl.nextSibling;
        if (nextSibling?.nodeType === 3 && !String(nextSibling.textContent || '').trim()) {
          nextSibling.remove();
        }
        linkEl.remove();
      });
      return doc.body.innerHTML.replace(/^<div>|<\/div>$/g, '');
    } catch (err) {
      console.warn('Failed removing internal links from html:', err);
    }
  }

  const escapedId = safeId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const escapedType = safeType.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return raw.replace(
    new RegExp(
      `<(?:a|span)\\b[^>]*data-amn-link\\s*=\\s*["']1["'][^>]*data-amn-link-type\\s*=\\s*["']${escapedType}["'][^>]*data-amn-link-id\\s*=\\s*["']${escapedId}["'][^>]*>.*?<\\/(?:a|span)>\\s*`,
      'gi'
    ),
    ''
  );
};

const normalizeTitle = (value = '', fallback = 'Untitled') => {
  const trimmed = String(value || '').trim();
  return trimmed || fallback;
};

const toPlainText = (value = '') =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();

const normalizeAncestorIds = (ancestorIds = []) =>
  [...new Set((Array.isArray(ancestorIds) ? ancestorIds : [])
    .map((id) => String(id || '').trim())
    .filter(Boolean))];

const ancestorIdsToAttr = (ancestorIds = []) => normalizeAncestorIds(ancestorIds).join(',');

export const ancestorIdsFromAttr = (value = '') =>
  normalizeAncestorIds(String(value || '').split(',').map((part) => part.trim()));

export const renderInternalLinksAsCardsInHtml = (html = '', notesById = {}, options = {}) => {
  const raw = String(html || '');
  if (!raw || !raw.includes(INTERNAL_LINK_ATTR)) return raw;
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return raw;
  const ancestorIds = normalizeAncestorIds(options?.ancestorIds || []);

  try {
    const doc = new window.DOMParser().parseFromString(`<div>${raw}</div>`, 'text/html');
    const links = doc.querySelectorAll(`[${INTERNAL_LINK_ATTR}="${INTERNAL_LINK_FLAG}"]`);
    links.forEach((linkEl) => {
      const payload = getInternalLinkPayloadFromElement(linkEl);
      if (!payload?.targetId || payload.targetType !== 'note') return;
      const wouldRecurse = ancestorIds.includes(payload.targetId);
      const source = notesById?.[payload.targetId] || null;
      if (wouldRecurse) {
        const recursiveCard = doc.createElement('div');
        recursiveCard.className = 'internal-note-link-card internal-note-link-card-grandchild internal-note-link-card-recursive';
        recursiveCard.setAttribute(INTERNAL_LINK_ATTR, INTERNAL_LINK_FLAG);
        recursiveCard.setAttribute(INTERNAL_LINK_TYPE_ATTR, 'note');
        recursiveCard.setAttribute(INTERNAL_LINK_ID_ATTR, payload.targetId);

        const recursiveTitle = doc.createElement('div');
        recursiveTitle.className = 'internal-note-link-card-title';
        const titleEl = doc.createElement('span');
        titleEl.className = 'internal-note-link-card-title-link';
        titleEl.textContent = source?.title || payload.title || 'Untitled Note';
        titleEl.setAttribute(INTERNAL_LINK_ATTR, INTERNAL_LINK_FLAG);
        titleEl.setAttribute(INTERNAL_LINK_TYPE_ATTR, 'note');
        titleEl.setAttribute(INTERNAL_LINK_ID_ATTR, payload.targetId);
        recursiveTitle.appendChild(titleEl);
        recursiveCard.appendChild(recursiveTitle);
        linkEl.replaceWith(recursiveCard);
        return;
      }

      const details = doc.createElement('details');
      details.className = 'internal-note-link-card';
      details.setAttribute(INTERNAL_LINK_ATTR, INTERNAL_LINK_FLAG);
      details.setAttribute(INTERNAL_LINK_TYPE_ATTR, 'note');
      details.setAttribute(INTERNAL_LINK_ID_ATTR, payload.targetId);
      // Store ancestors of this card target (excluding the target itself).
      details.setAttribute('data-amn-ancestors', ancestorIdsToAttr(ancestorIds));

      const summary = doc.createElement('summary');
      summary.className = 'internal-note-link-card-title';
      const arrowEl = doc.createElement('span');
      arrowEl.className = 'internal-note-link-card-arrow';
      arrowEl.setAttribute('aria-hidden', 'true');
      summary.appendChild(arrowEl);

      const titleEl = doc.createElement('span');
      titleEl.className = 'internal-note-link-card-title-link';
      titleEl.textContent = source?.title || payload.title || 'Untitled Note';
      titleEl.setAttribute(INTERNAL_LINK_ATTR, INTERNAL_LINK_FLAG);
      titleEl.setAttribute(INTERNAL_LINK_TYPE_ATTR, 'note');
      titleEl.setAttribute(INTERNAL_LINK_ID_ATTR, payload.targetId);
      summary.appendChild(titleEl);
      details.appendChild(summary);

      const bodyEl = doc.createElement('div');
      bodyEl.className = 'internal-note-link-card-body';
      bodyEl.setAttribute('data-amn-lazy-body', '1');
      bodyEl.setAttribute('data-amn-link-id', payload.targetId);
      details.appendChild(bodyEl);
      linkEl.replaceWith(details);
    });
    return doc.body.innerHTML.replace(/^<div>|<\/div>$/g, '');
  } catch (err) {
    console.warn('Failed to render internal note cards:', err);
    return raw;
  }
};

export const getEmbeddedNoteBodyVersion = (targetId = '', notesById = {}) => {
  const safeId = String(targetId || '').trim();
  if (!safeId) return '';
  const source = notesById?.[safeId] || null;
  const updatedAt = String(source?.updatedAt || '').trim();
  const notesLength = String(source?.notes || '').length;
  return `${safeId}::${updatedAt || 'na'}::${notesLength}`;
};

export const buildEmbeddedNoteBodyHtml = (targetId = '', notesById = {}, ancestorIds = []) => {
  const safeId = String(targetId || '').trim();
  if (!safeId) return '<p>No content yet.</p>';
  const safeAncestors = normalizeAncestorIds(ancestorIds);
  if (safeAncestors.includes(safeId)) return '<p>Recursive link hidden.</p>';
  const source = notesById?.[safeId] || null;
  const sourceRaw = String(source?.notes || '');
  let rendered = sourceRaw;
  if (!sourceRaw) {
    return '<p>No content yet.</p>';
  }

  if (!/<\/?[a-z][\w-]*(\s[^>]*)?>/i.test(sourceRaw)) {
    rendered = sourceRaw
      .split('\n')
      .map((line) => `<p>${line ? escapeHtml(line) : '<br>'}</p>`)
      .join('');
  }

  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return rendered;
  }

  try {
    return renderInternalLinksAsCardsInHtml(rendered, notesById, {
      ancestorIds: [...safeAncestors, safeId],
    });
  } catch (err) {
    console.warn('Failed to build embedded note body:', err);
    return rendered;
  }
};

export const getCachedEmbeddedNoteBodyHtml = (targetId = '', notesById = {}, ancestorIds = []) => {
  const safeAncestors = normalizeAncestorIds(ancestorIds);
  const version = `${getEmbeddedNoteBodyVersion(targetId, notesById)}::${ancestorIdsToAttr(safeAncestors)}`;
  if (!version) return '<p>No content yet.</p>';
  const cached = embeddedBodyHtmlCache.get(version);
  if (cached) return cached;

  const built = buildEmbeddedNoteBodyHtml(targetId, notesById, safeAncestors);
  embeddedBodyHtmlCache.set(version, built);
  if (embeddedBodyHtmlCache.size > EMBEDDED_BODY_CACHE_LIMIT) {
    const oldestKey = embeddedBodyHtmlCache.keys().next().value;
    if (oldestKey) embeddedBodyHtmlCache.delete(oldestKey);
  }
  return built;
};

const toSearchFields = (item = {}, kindLabel = 'Note') => {
  const tags = Array.isArray(item.customTags) ? item.customTags.join(' ') : '';
  const extraLinks = Array.isArray(item.links) ? item.links.join(' ') : '';
  const fields = [
    { label: 'Title', text: item.title || '' },
    { label: 'Notes', text: toPlainText(item.notes || '') },
    { label: 'Category', text: item.genre || '' },
    { label: 'Type', text: item.type || '' },
    { label: 'Status', text: item.status || '' },
    { label: 'Rating', text: `${item.rating || ''} ${item.ratingLabel || ''}`.trim() },
    { label: 'Tags', text: tags },
    { label: 'Quick Note', text: item.quickNote || '' },
    { label: 'Event Date', text: item.eventDate || '' },
    { label: 'Source', text: item.source || '' },
    { label: 'Learned From', text: item.learnedFrom || '' },
    { label: 'Link', text: `${item.link1 || ''} ${item.link2 || ''} ${item.link3 || ''} ${extraLinks}`.trim() },
    { label: `${kindLabel} Metadata`, text: `${item.updatedAt || ''} ${item.createdAt || ''}`.trim() },
  ];
  return fields
    .map((field) => ({ ...field, text: String(field.text || '').trim() }))
    .filter((field) => Boolean(field.text));
};

export const toInternalLinkTargetOptions = ({ notes = [], folders = [], exclude = null } = {}) => {
  const excludedType = exclude?.targetType ? normalizeInternalLinkType(exclude.targetType) : null;
  const excludedId = String(exclude?.targetId || '').trim();

  const normalizeNote = (item) => ({
    id: item.id,
    title: normalizeTitle(item.title, 'Untitled Note'),
    targetType: 'note',
    searchFields: toSearchFields(item, 'Note'),
  });

  const normalizeFolder = (item) => ({
    id: item.id,
    title: normalizeTitle(item.title, 'Untitled Folder'),
    targetType: 'folder',
    searchFields: toSearchFields(item, 'Folder'),
  });

  const all = [
    ...notes.map(normalizeNote),
    ...folders.map(normalizeFolder),
  ].filter((item) => {
    if (!item.id) return false;
    if (!excludedId || !excludedType) return true;
    return !(item.id === excludedId && item.targetType === excludedType);
  });

  all.sort((a, b) => {
    if (a.targetType !== b.targetType) return a.targetType === 'note' ? -1 : 1;
    return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
  });

  return all;
};

export const findInboundInternalLinks = ({ targetType = 'note', targetId = '', notes = [], folders = [] } = {}) => {
  const safeTargetType = normalizeInternalLinkType(targetType);
  const safeTargetId = String(targetId || '').trim();
  if (!safeTargetId) return [];

  const sources = [
    ...notes.map((n) => ({ sourceType: 'note', ...n })),
    ...folders.map((f) => ({ sourceType: 'folder', ...f })),
  ];

  const merged = new Map();

  sources.forEach((source) => {
    const links = extractInternalLinksFromHtml(source.notes || '');
    const pointsToTarget = links.some((link) => link.targetType === safeTargetType && link.targetId === safeTargetId);
    if (!pointsToTarget) return;
    if (!source.id) return;

    const key = `${source.sourceType}:${source.id}`;
    if (merged.has(key)) return;

    merged.set(key, {
      sourceType: source.sourceType,
      sourceId: source.id,
      sourceTitle: normalizeTitle(source.title, source.sourceType === 'folder' ? 'Untitled Folder' : 'Untitled Note'),
      href: getInternalEntityHref(source.sourceType, source.id),
    });
  });

  return Array.from(merged.values()).sort((a, b) => a.sourceTitle.localeCompare(b.sourceTitle, undefined, { sensitivity: 'base' }));
};
